/**
 * Axios client for the fasto_bi service endpoints (`/svc/*`) — the FastAPI
 * backend that also serves this SPA, so the base URL is always same-origin.
 * In `vite dev`, `/svc` is proxied to the local FastAPI (see vite.config.ts).
 *
 * Auth mirrors the geo client: the service verifies the SAME Django JWT, so
 * we reuse the in-memory access token from `@/api/client`. On 401 we ping a
 * cheap Django endpoint through `apiClient` (whose interceptor refreshes +
 * retries), then replay the original request with the fresh token.
 */
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { apiClient, getAccessToken } from '@/api/client';

export const svcClient: AxiosInstance = axios.create({
  baseURL: '',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

svcClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token && !config.headers.has?.('Authorization')) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  if (config.data instanceof FormData) {
    // Let browser/axios generate multipart/form-data with the correct boundary
    delete config.headers['Content-Type'];
  }
  return config;
});

svcClient.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config as (InternalAxiosRequestConfig & { _svcRetry?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._svcRetry) {
      original._svcRetry = true;
      try {
        // Any authed Django call triggers the main client's refresh flow.
        await apiClient.get('/feature/bi/api/access/');
        const fresh = getAccessToken();
        if (fresh) {
          original.headers.set('Authorization', `Bearer ${fresh}`);
          return svcClient.request(original);
        }
      } catch {
        // fall through to the original rejection
      }
    }
    return Promise.reject(error);
  },
);

export async function svcGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const r = await svcClient.get<T>(url, config);
  return r.data;
}

export async function svcPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const r = await svcClient.post<T>(url, body, config);
  return r.data;
}

export async function svcPatch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const r = await svcClient.patch<T>(url, body, config);
  return r.data;
}
